import { createClient } from '@supabase/supabase-js'
import { isNative } from './utils/platform'

const supabaseUrl = process.env.REACT_APP_SUPABASE_URL
const supabaseAnonKey = process.env.REACT_APP_SUPABASE_ANON_KEY

// Mobile Safari (private browsing / restricted storage) can throw
// InvalidAccessError on localStorage.{get,set,remove}Item, which surfaces
// as an unhandled promise rejection inside Supabase's GoTrueClient.
// This adapter swallows those exceptions and falls back to in-memory
// storage so auth flows don't crash the app.
const memoryStore = new Map()

const safeStorage = {
  getItem: (key) => {
    try {
      return window.localStorage.getItem(key)
    } catch {
      return memoryStore.has(key) ? memoryStore.get(key) : null
    }
  },
  setItem: (key, value) => {
    try {
      window.localStorage.setItem(key, value)
    } catch {
      memoryStore.set(key, value)
    }
  },
  removeItem: (key) => {
    try {
      window.localStorage.removeItem(key)
    } catch {
      memoryStore.delete(key)
    }
  },
}

// Supabase JS wraps every authed request in a navigator.locks acquire for
// cross-tab session safety. In the NATIVE app there is exactly one JS
// context (no tabs), and WKWebView's Web Locks implementation can wedge —
// when it does, every query in the app awaits forever ("Loading…" on every
// surface until force-quit; DrillSearchPanel documented this exact hang on
// 2026-05-19). A pass-through lock removes navigator.locks from the native
// path entirely. Web keeps the real lock for multi-tab safety.
const passThroughLock = async (_name, _acquireTimeout, fn) => await fn()

// Sign-in requests get a deadline. auth-js refreshes the token while holding
// its auth lock, and every query in the tab waits on that lock to read its
// token, so one refresh request that never answers (a dropped connection, a
// laptop waking from sleep) held up every save and every AI call in the tab
// with no error at all: AFS showed "The database didn't answer" and the mic
// sent nothing (Coleman, 2026-10-22). Aborted, the refresh fails like any
// network error and auth-js retries it.
//
// Database requests get one too (Coleman, 2026-10-23: saves stuck on "The
// database didn't answer", then a game that opened to a black screen). A
// request that never answers (a connection that died while the laptop slept,
// or the network changed under it) used to wait forever: the save never
// failed so never retried, and the hung connections stayed open for the whole
// browser, so other platform tabs queued behind them too. The database itself
// stops any statement after 8 seconds, so 20 is never cutting off real work;
// past it the request fails like any network error, and its caller retries.
// Storage uploads and edge functions are left alone: those can rightly run long.
const AUTH_FETCH_MS = 15000
const REST_FETCH_MS = 20000
export const deadlineFor = (url) => (url.includes('/auth/v1/') ? AUTH_FETCH_MS : url.includes('/rest/v1/') ? REST_FETCH_MS : null)
export const deadlineFetch = (input, init = {}) => {
  const url = typeof input === 'string' ? input : input?.url || ''
  const ms = deadlineFor(url)
  if (!ms || init.signal || typeof AbortController === 'undefined') return fetch(input, init)
  const ctl = new AbortController()
  const timer = setTimeout(() => ctl.abort(), ms)
  return fetch(input, { ...init, signal: ctl.signal }).finally(() => clearTimeout(timer))
}

// Public by design (it ships in every page). Used as the bearer for edge
// functions a signed-out visitor may call, like guest cert checkout.
export const SUPABASE_ANON_KEY = supabaseAnonKey

export const supabase = createClient(supabaseUrl, supabaseAnonKey, {
  auth: {
    storage: safeStorage,
    persistSession: true,
    autoRefreshToken: true,
    ...(isNative() ? { lock: passThroughLock } : {}),
  },
  global: { fetch: deadlineFetch },
})

// ── Auth listeners run after the auth lock, never inside it ────────────────
//
// auth-js calls every onAuthStateChange listener while it still holds its auth
// lock (a token refresh, about hourly, fires TOKEN_REFRESHED from inside it)
// and waits for each listener to finish. A listener that waits on Supabase in
// turn (useUserAddons re-read the user's add-ons) waits for that same lock:
// neither can finish, and from then on every query in the tab waits forever.
// That was AFS's "The database didn't answer" (Coleman, 2026-10-22): the game
// stopped saving about an hour in, every time, until the page was reloaded.
// Each listener now runs on the next tick, after the lock is released, and is
// never waited on.
const rawOnAuthStateChange = supabase.auth.onAuthStateChange.bind(supabase.auth)
supabase.auth.onAuthStateChange = (callback) => rawOnAuthStateChange((event, session) => {
  setTimeout(() => {
    try {
      const out = callback(event, session)
      if (out && typeof out.catch === 'function') out.catch(e => console.error('[auth listener]', e))
    } catch (e) { console.error('[auth listener]', e) }
  }, 0)
})

/**
 * The signed-in coach's access token, for calling our own /api endpoints,
 * without waiting behind the auth lock. getSession() normally answers at
 * once; if it hasn't in `waitMs` (the lock is held by a refresh that's still
 * out), the stored session is read directly, as long as its token hasn't
 * expired. The server checks the token either way.
 */
export async function accessToken(waitMs = 2000) {
  const viaLock = supabase.auth.getSession().then(({ data }) => data?.session?.access_token || null, () => null)
  const late = Symbol('late')
  const first = await Promise.race([viaLock, new Promise(r => setTimeout(() => r(late), waitMs))])
  if (first !== late) return first
  try {
    const stored = JSON.parse(safeStorage.getItem(supabase.auth.storageKey) || 'null')
    if (stored?.access_token && (stored.expires_at || 0) * 1000 > Date.now() + 10000) return stored.access_token
  } catch { /* unreadable: wait for the lock after all */ }
  return viaLock
}

// ── getUser() without the round trip ──────────────────────────────────────
//
// auth-js holds its auth lock for the whole of getUser(): a network request
// to /auth/v1/user. Every query in the app needs that same lock to read its
// access token, so while one getUser() is in flight NOTHING else can talk to
// Supabase. There are ~80 getUser() calls (loadUserData makes one per key),
// most of them at boot, so they ran one after another and the community feed
// sat behind the queue: twenty seconds on a slow connection (Coleman,
// 2026-10-07: "people will never use it if it takes that long").
//
// Nearly every caller only wants "who am I", which the stored session
// already knows. So a plain getUser() answers from the session, which is a
// local read; with no session it falls through to the real call, and
// getUser(jwt) (validating a specific token) always goes to the server.
// Nothing gets less safe: the database checks the JWT on every request, and
// a session the server has revoked still fails its refresh and signs out.
const serverGetUser = supabase.auth.getUser.bind(supabase.auth)
supabase.auth.getUser = async (jwt) => {
  if (jwt) return serverGetUser(jwt)
  try {
    const { data } = await supabase.auth.getSession()
    if (data?.session?.user) return { data: { user: data.session.user }, error: null }
  } catch { /* fall through to the server */ }
  return serverGetUser()
}
/** The original, for the rare caller that must hear it from the server. */
supabase.auth.getUserFromServer = serverGetUser
